import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  ClipboardList,
  FileText,
  House,
  LayoutDashboard,
  MessageSquare,
  Settings,
  User,
  Users,
  BarChart2,
} from 'lucide-react';
import AppShell, { type NavItem } from './components/AppShell';
import Spinner from './components/Spinner';
import { useAuth } from './components/AuthProvider';
import { homePathForRole, type AccountRole } from './lib/supabase';

/* Public */
import Landing from './routes/Landing';
import Login from './routes/auth/Login';
import Signup from './routes/auth/Signup';
import ForgotPassword from './routes/auth/ForgotPassword';
import ResetPassword from './routes/auth/ResetPassword';
import ProviderRequest from './routes/auth/ProviderRequest';
import NotFound from './routes/NotFound';

/* Client */
import ClientHome from './routes/client/Home';
import ClientBookings from './routes/client/Bookings';
import BookingDetails from './routes/client/BookingDetails';
import CreateBooking from './routes/client/CreateBooking';
import ProvidersList from './routes/client/ProvidersList';
import ProviderProfile from './routes/client/ProviderProfile';
import ClientProfile from './routes/client/Profile';
import MessagesLayout from './routes/shared/MessagesLayout';
import ChatRoom from './routes/shared/ChatRoom';
import NoConversation from './routes/shared/NoConversation';

/* Provider */
import ProviderDashboard from './routes/provider/Dashboard';
import ProviderRequests from './routes/provider/Requests';
import ApplyBooking from './routes/provider/ApplyBooking';
import ProviderReviews from './routes/provider/Reviews';
import ProviderAccount from './routes/provider/Account';

/* Admin */
import AdminApplications from './routes/admin/Applications';
import AdminStats from './routes/admin/Stats';
import AdminProviders from './routes/admin/Providers';
import AdminSettings from './routes/admin/Settings';
import AdminChat from './routes/admin/AdminChat';

const CLIENT_NAV: NavItem[] = [
  { to: '/app', label: 'Accueil', icon: House, end: true },
  { to: '/app/bookings', label: 'Demandes', icon: FileText },
  { to: '/app/messages', label: 'Messages', icon: MessageSquare },
  { to: '/app/profile', label: 'Profil', icon: User },
];

const PROVIDER_NAV: NavItem[] = [
  { to: '/pro', label: 'Missions', icon: LayoutDashboard, end: true },
  { to: '/pro/requests', label: 'Demandes', icon: ClipboardList },
  { to: '/pro/messages', label: 'Messages', icon: MessageSquare },
  { to: '/pro/profile', label: 'Mon profil', icon: User },
];

const ADMIN_NAV: NavItem[] = [
  { to: '/admin', label: 'Demandes', icon: ClipboardList, end: true },
  { to: '/admin/providers', label: 'Prestataires', icon: Users },
  { to: '/admin/stats', label: 'Statistiques', icon: BarChart2 },
  { to: '/admin/settings', label: 'Admin', icon: Settings },
];

/** Blocks an area until the session is known and the role matches. */
function RequireRole({ role, children }: { role: AccountRole; children: React.ReactNode }) {
  const { session, role: userRole, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Spinner block label="Chargement..." />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (userRole && userRole !== role) return <Navigate to={homePathForRole(userRole)} replace />;

  return <>{children}</>;
}

/** Keeps signed-in users out of the auth screens. */
function RedirectIfSignedIn({ children }: { children: React.ReactNode }) {
  const { session, role, loading } = useAuth();
  if (loading) return <Spinner block label="Chargement..." />;
  if (session && role) return <Navigate to={homePathForRole(role)} replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route
        path="/"
        element={
          <RedirectIfSignedIn>
            <Landing />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="/login"
        element={
          <RedirectIfSignedIn>
            <Login />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="/signup"
        element={
          <RedirectIfSignedIn>
            <Signup />
          </RedirectIfSignedIn>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/provider-request" element={<ProviderRequest />} />

      {/* Client area */}
      <Route
        path="/app"
        element={
          <RequireRole role="client">
            <AppShell
              items={CLIENT_NAV}
              logo="/assets/brand/logo.png"
              bellRole="client"
              areaLabel="Espace client"
            />
          </RequireRole>
        }
      >
        <Route index element={<ClientHome />} />
        <Route path="bookings" element={<ClientBookings />} />
        <Route path="bookings/:id" element={<BookingDetails />} />
        <Route path="new-request" element={<CreateBooking />} />
        <Route path="providers" element={<ProvidersList />} />
        <Route path="providers/:id" element={<ProviderProfile />} />
        <Route path="profile" element={<ClientProfile />} />
        <Route path="messages" element={<MessagesLayout area="client" />}>
          <Route index element={<NoConversation />} />
          <Route path=":roomId" element={<ChatRoom area="client" />} />
        </Route>
      </Route>

      {/* Provider area */}
      <Route
        path="/pro"
        element={
          <RequireRole role="provider">
            <AppShell
              items={PROVIDER_NAV}
              logo="/assets/presta/logo.png"
              bellRole="provider"
              areaLabel="Espace prestataire"
            />
          </RequireRole>
        }
      >
        <Route index element={<ProviderDashboard />} />
        <Route path="requests" element={<ProviderRequests />} />
        <Route path="requests/:bookingId/apply" element={<ApplyBooking />} />
        <Route path="reviews" element={<ProviderReviews />} />
        <Route path="profile" element={<ProviderAccount />} />
        <Route path="messages" element={<MessagesLayout area="provider" />}>
          <Route index element={<NoConversation />} />
          <Route path=":roomId" element={<ChatRoom area="provider" />} />
        </Route>
      </Route>

      {/* Admin area */}
      <Route
        path="/admin"
        element={
          <RequireRole role="admin">
            <AppShell items={ADMIN_NAV} logo="/assets/brand/logo.png" areaLabel="Administration" />
          </RequireRole>
        }
      >
        <Route index element={<AdminApplications />} />
        <Route path="providers" element={<AdminProviders />} />
        <Route path="stats" element={<AdminStats />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="chat/:userId" element={<AdminChat />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
